import React, { useState, useEffect, useCallback } from "react";
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonButtons,
  IonMenuButton,
  IonLoading,
} from "@ionic/react";
import { useHistory } from "react-router-dom";
import {
  DataGrid,
  GridColDef,
  GridActionsCellItem,
  GridPaginationModel,
} from "@mui/x-data-grid";
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Button,
} from "@mui/material";
import { Edit, Delete, Search, Visibility, Refresh } from "@mui/icons-material";
import { useAssets } from "../../../../hooks/useAssets";
import { assetCategories } from "../../../../common/consts";
import { formatCurrencyN } from "../../../../common/utils_t";
import ConfirmationModal from "../../../../components/ConfirmationModal";
// import { useAssets } from "../../hooks/useAssets";
// import ConfirmationModal from "../../components/ConfirmationModal";
// import { formatCurrencyN } from "../../../../common/utils_t";
// import { assetCategories } from "../../utils/consts";

const AssetRegisterList: React.FC = () => {
  const history = useHistory();
  const {
    assets,
    loading,
    pagination,
    fetchAssets,
    searchAssets,
    deleteAsset,
    totalInventoryValue,
  } = useAssets();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState<string>("");
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });

  const handleView = (asset_id: string) => {
    history.push(`/core-accounting/asset-register/${asset_id}`);
  };

  const handleEdit = (asset_id: string) => {
    history.push(`/core-accounting/asset-register/${asset_id}/edit`);
  };

  const handleDeleteClick = (id: string) => {
    setSelectedAssetId(id);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedAssetId) {
      await deleteAsset(selectedAssetId);
      // Refresh current page after delete
      if (searchInput) {
        await searchAssets(
          searchInput,
          paginationModel.page + 1,
          paginationModel.pageSize
        );
      } else {
        await fetchAssets(paginationModel.page + 1, paginationModel.pageSize);
      }
      setDeleteModalOpen(false);
      setSelectedAssetId(null);
    }
  };

  const handleSearch = useCallback(
    async (query: string) => {
      setSearchInput(query);
      if (query.trim()) {
        await searchAssets(query, 1, paginationModel.pageSize);
        setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
      } else {
        await fetchAssets(1, paginationModel.pageSize);
        setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
      }
    },
    [searchAssets, fetchAssets, paginationModel.pageSize]
  );

  const handlePaginationModelChange = useCallback(
    async (newModel: GridPaginationModel) => {
      setPaginationModel(newModel);
      const page = newModel.page + 1; // API uses 1-based pagination
      const limit = newModel.pageSize;

      if (searchInput.trim()) {
        await searchAssets(searchInput, page, limit);
      } else {
        await fetchAssets(page, limit);
      }
    },
    [searchInput, searchAssets, fetchAssets]
  );

  // Initialize data on mount
  useEffect(() => {
    fetchAssets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setSelectedAssetId(null);
  };

  const getCategoryName = (categoryId: string | undefined): string => {
    if (!categoryId) return "";
    // Handle both string and number IDs
    const category = assetCategories.find(
      (cat) => cat.id === String(categoryId) || cat.id === categoryId
    );
    return category?.name || String(categoryId);
  };

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "id_tag",
      headerName: "ID Tag",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "category_id",
      headerName: "Category",
      flex: 1,
      minWidth: 150,
      renderCell: (params: any) => {
        const categoryId = params.row?.category_id;
        return <span>{getCategoryName(categoryId)}</span>;
      },
    },
    {
      field: "dept_area",
      headerName: "Dept/Area",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "room",
      headerName: "Room",
      flex: 1,
      minWidth: 100,
    },
    {
      field: "purchase_date",
      headerName: "Date",
      flex: 1,
      minWidth: 120,
      renderCell: (params: any) => {
        if (!params.row?.purchase_date) return <span></span>;
        return (
          <span>
            {new Date(params.row.purchase_date as string).toLocaleDateString()}
          </span>
        );
      },
    },
    {
      field: "supplier",
      headerName: "Supplier",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "warranty_expiration",
      headerName: "Warranty Expiration",
      flex: 1,
      minWidth: 150,
      renderCell: (params: any) => {
        if (!params.row?.warranty_expiration) return <span></span>;
        return (
          <span>
            {new Date(
              params.row.warranty_expiration as string
            ).toLocaleDateString()}
          </span>
        );
      },
    },
    {
      field: "asset_condition",
      headerName: "Condition",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "unit_value",
      headerName: "Unit Value",
      flex: 1,
      minWidth: 120,
      renderCell: (params: any) => {
        return <span>{formatCurrencyN(params.row?.unit_value as number)}</span>;
      },
    },
    {
      field: "quantity",
      headerName: "Qty",
      flex: 1,
      minWidth: 80,
    },
    {
      field: "value",
      headerName: "Value",
      flex: 1,
      minWidth: 120,
      renderCell: (params: any) => {
        return <span>{formatCurrencyN(params.row?.value as number)}</span>;
      },
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      minWidth: 100,
      getActions: (params: any) => {
        return [
          <GridActionsCellItem
            icon={<Visibility />}
            label="View"
            onClick={() => handleView(params.row.asset_id)}
          />,
          <GridActionsCellItem
            icon={<Edit />}
            label="Edit"
            onClick={() => handleEdit(params.row.asset_id)}
          />,
          <GridActionsCellItem
            icon={<Delete />}
            label="Delete"
            onClick={() => handleDeleteClick(params.row.asset_id)}
          />,
        ];
      },
    },
  ];

  return (
    <IonPage className="pushDown">
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>Asset Register</IonTitle>
          <IonButtons slot="end">
            <IonButton
              color="primary"
              fill="outline"
              onClick={() =>
                history.push("/core-accounting/asset-register/create")
              }
            >
              Add Asset
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <Box sx={{ p: 2 }}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Box>
                <Typography variant="h6">Asset Register</Typography>
                <Typography variant="body2" color="text.secondary">
                  Date: {new Date().toLocaleDateString()}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" align="right">
                  Total Asset Value
                </Typography>
                <Typography
                  variant="h4"
                  align="right"
                  color="primary"
                  sx={{ fontWeight: "bold" }}
                >
                  {formatCurrencyN(totalInventoryValue())}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mb: 2, display: "flex", gap: 2, alignItems: "center" }}>
              <TextField
                fullWidth
                placeholder="Search assets by name, description, or supplier..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSearch(searchInput);
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{ maxWidth: 600 }}
              />
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={() => {
                  setSearchInput("");
                  fetchAssets(
                    paginationModel.page + 1,
                    paginationModel.pageSize
                  );
                }}
                disabled={loading}
              >
                Refresh
              </Button>
            </Box>

            <Box sx={{ width: "100%", overflowX: "auto" }}>
              <DataGrid
                rows={assets}
                columns={columns}
                loading={loading}
                getRowId={(row) => row.asset_id || row.id}
                paginationMode="server"
                rowCount={pagination.total}
                pageSizeOptions={[10, 25, 50, 100]}
                paginationModel={paginationModel}
                onPaginationModelChange={handlePaginationModelChange}
                disableRowSelectionOnClick
                autoHeight={true}
                sx={{
                  "& .MuiDataGrid-cell": {
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                  },
                }}
              />
            </Box>
          </Paper>
        </Box>

        <ConfirmationModal
          isOpen={deleteModalOpen}
          header="Delete Asset"
          message="Are you sure you want to delete this asset? This action cannot be undone."
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
        />

        <IonLoading isOpen={loading} message="Loading assets..." />
      </IonContent>
    </IonPage>
  );
};

export default AssetRegisterList;
